export default {
	process: {
		empty: {
			title: "No processes yet",
			hint: "Start one from a process the project defines.",
		},
		// The search and the status chips above the list.
		filter: {
			search: "Search processes…",
			status: "Status",
			clear: "Clear filters",
			found: "{n} found",
			noResults: {
				title: "No matching processes",
				hint: "Nothing matches this search and these filters. Try another term, or clear them.",
			},
		},
		new: "New process",
		create: "Create process",
		createFailed: "The process could not be created.",
		base: "Process (from the Designer)",
		name: "Instance name",
		nameHint: "Leave empty for a generated name.",
		// What it is called: its name, description and — for whoever holds `dataspace:processes:alias` —
		// its alias (`EditProcessDialog`). The menu says "details", which is true for everyone who sees it.
		edit: {
			action: "Edit details",
			title: "Edit process",
			saved: "Saved {name}.",
		},
		// Whom one process is shared with beyond their role (`ProcessAccessDialog`, and the start
		// dialog's section). "Take part" is answering its questions and changing its data.
		access: {
			action: "Shared with",
			title: "Shared with",
			hint: "Whoever's role shows processes sees this one anyway. Here it is shared with people whose role does not: to view it, or to view it and take part — answer its questions and change its data.",
			startHint:
				"Share it from the start with people whose role does not show them processes.",
			none: "Not shared with anybody beyond their role.",
			grants: {
				read: "Can view",
				read_write: "Can view and take part",
			},
			grant: "Access",
			grantFor: "Access for {name}",
			person: "Person",
			personPlaceholder: "Search by name or email…",
			nobodyLeft: "Nobody found.",
			add: "Share",
			remove: "Stop sharing with {name}",
			shared: "Shared with {name}.",
			unshared: "No longer shared with {name}.",
		},
		// A process another one spawned, in the list: started by that process, not by its person.
		spawnedBy: {
			note: "By a process · {date}",
			title: "Started by the process “{name}”",
		},
		deleteConfirm: "Delete {name}?",
		deleteNote: "Its saved state and logs go with it. This cannot be undone.",
		stopNamed: "Stop {name}",
		column: {
			process: "Process",
			status: "Status",
			definition: "Definition",
			dataobjects: "Dataobjects",
			created: "Created",
			version: "Version",
			resume: "On restart",
		},
		toast: {
			stopped: "Stopped {name}.",
			archived: "Archived {name}.",
			unarchived: "Unarchived {name}.",
			deleted: "Deleted {name}.",
			restartRuleSaved: "Restart rule saved.",
			upgraded: "Upgrading {name} to {version}.",
		},
		archive: "Archive",
		unarchive: "Unarchive",
		archived: "Archived",
		selection: {
			row: "Select {name}",
			clear: "Clear selection",
			all: "Select all",
			none: "Deselect all",
			count: "{n} selected",
		},
		bulk: {
			archive: "Archive",
			deleteConfirm: "Delete {n} processes?",
			archived: "Archived {n} process. | Archived {n} processes.",
			deleted: "Deleted {n} process. | Deleted {n} processes.",
			archiveFailed:
				"{n} process could not be archived. | {n} processes could not be archived.",
			deleteFailed: "{n} process could not be deleted. | {n} processes could not be deleted.",
		},
		error: {
			showDetails: "Show details",
			hideDetails: "Hide details",
			copy: "Copy",
			copied: "Copied",
			showFor: "Why {name} failed",
		},
		status: {
			starting: "Starting",
			running: "Running",
			stopped: "Stopped",
			failed: "Failed",
			archived: "Archived",
		},
		restartRule: {
			label: "Restart rule",
			no: "No restart",
			byUser: "Manual (by user)",
			unlessStopped: "Always, unless stopped",
			onFailure: "On failure",
			onFailureWithRetries: "On failure ({n}x)",
			retries: "Maximum retries",
			retriesHelp: "How often it is restarted after a failure. Leave blank for no limit.",
			retriesInvalid: "Enter a whole number of 1 or more, or leave it blank for no limit.",
		},
		restartRuleAction: "Restart rule…",
		// The version a process runs: the release it was created on, or `wip`.
		version: {
			wipHint: "Follows the project's work in progress. A restart runs its latest build.",
			releaseHint:
				"Created on release {version}, and stays on it even when the dataspace moves to another version.",
			outdated: "Old version",
			outdatedHint:
				"The dataspace runs {current} now, and this process still runs {version}. Upgrade it to bring it along.",
			outdatedHintUnnamed:
				"The dataspace is on another version now, and this process still runs {version}. Upgrade it to bring it along.",
		},
		// What a restart does with the process's saved state (see `utils/process/resume.ts`).
		resume: {
			unsupported: {
				label: "Built before resume — restarts from scratch",
				hint: "This build was made before processes could continue where they stopped, so a restart starts it from scratch and what it holds now is lost.",
			},
			blockedHint:
				"It is not restarted by itself. A restart by hand tries to continue again; it does not start from scratch.",
			blockedBy: {
				format_unsupported: "Blocked: unknown state format",
				snapshot_invalid: "Blocked: saved state damaged",
				schema_mismatch: "Blocked: data model changed",
				dataobject_unrestorable: "Blocked: data not restorable",
				restore_error: "Blocked: restoring failed",
				restore_failed: "Blocked: restoring failed",
				resume_state_missing: "Blocked: saved state missing",
				unstable: "Blocked: keeps failing",
				unknown: "Blocked: {code}",
			},
			blockedWhy: {
				format_unsupported:
					"The saved state is in a format the engine of this build does not know.",
				snapshot_invalid: "The engine could not read the saved state.",
				schema_mismatch:
					"The saved state was made for a different data model than this build has.",
				dataobject_unrestorable:
					"One of the process's dataobjects could not be rebuilt from the saved state.",
				restore_error:
					"The engine failed while taking the saved state back. The process's log has the details.",
				restore_failed:
					"The process ended while taking its saved state back, without saying why. Its log may have the details.",
				resume_state_missing:
					"The stored state is gone or damaged, and so is the copy before it. It is not replaced by a fresh start, because everything the process held would be lost.",
				unstable:
					"It exited three times in a row soon after taking its saved state back, so it is not restarted by itself any more.",
				unknown: "The engine reported “{code}” while taking the saved state back.",
			},
			lost: {
				label: "Saved state not used",
				hint: "It was handed its saved state when it started, and began from scratch anyway.",
			},
			stale: {
				/* In the process view's header: what a restart does. */
				restart: "Restart continues from an older state",
				label: "Continues from an older state",
				hint: "Its newest state could not be saved — it has grown past what can be stored, or the server could not write it — so a restart continues from the one before.",
			},
			saved: {
				/* In the process view's header: what a restart does. */
				restart: "Restart continues",
				label: "Continues where it stopped",
				hint: "A restart continues from the state saved last.",
			},
			none: {
				label: "Nothing saved yet",
				hintRunning: "It saves its state as it changes; nothing is stored yet.",
				hintStopped:
					"Nothing was stored before it stopped, so a restart starts from scratch.",
			},
			savedAt: "Saved {when}.",
			// A run that took its state back best-effort: none, one or n items no longer fitted.
			migrated: {
				/* In the process view's header, whose note says the rest. */
				chip: "Continued",
				label: "Continued best-effort: nothing dropped | Continued best-effort: {n} item dropped | Continued best-effort: {n} items dropped",
				hint: "This run took its saved state back best-effort, and everything still fitted. | This run took its saved state back best-effort, and {n} item no longer fitted this version. | This run took its saved state back best-effort, and {n} items no longer fitted this version.",
				title: "Continued best-effort",
				body: "The saved state was taken back as far as it fits this version, and all of it did. | The saved state was taken back as far as it fits this version: {n} item no longer fitted and was dropped. The process's log lists it. | The saved state was taken back as far as it fits this version: {n} items no longer fitted and were dropped. The process's log lists them.",
			},
		},
		// Bringing a process to the version its dataspace is on.
		upgrade: {
			action: "Upgrade to current version",
			title: "Upgrade {name} to {version}?",
			titleUnnamed: "Upgrade {name} to the dataspace's version?",
			note: "The process stops, then starts again on the dataspace's version and takes its saved data along best-effort: whatever no longer fits that version is dropped, and its log lists what. If that version's build was made before resume, there is no state to take and the process starts from scratch. This cannot be undone — a process cannot be taken back to its old version.",
			confirm: "Upgrade",
			refused: {
				restartNotAllowed:
					"Its restart rule is “No restart”, and an upgrade is a restart. Change the rule first.",
				archived: "An archived process cannot be upgraded. Unarchive it first.",
				alreadyCurrent: "It already runs the dataspace's version.",
				versionNotBuilt:
					"The dataspace's version has no build to run. Build that version first.",
				forbidden: "You may not manage the processes of this dataspace.",
				gone: "This process no longer exists.",
			},
		},
		// A running process started before its dataspace's environment changed.
		env: {
			outdated: "Old environment",
			changedSince: "Changed since it started: {names}",
			changedSinceUnnamed: "Its environment changed since it started",
		},
		restart: "Restart",
		stop: "Stop",
		// The Alternis chat. Most texts are alternis_voip's own (client/public/locales/en.json,
		// dev @ 128cfc3), so the conversation reads the same in both products.
		chat: {
			title: "Chat",
			empty: "No messages yet",
			emptyNote: "What is said in the call appears here, transcribed and translated.",
			waiting: "No call yet",
			waitingNote:
				"The conversation appears here as soon as the process is connected to a call.",
			transcribing: "Transcription in progress",
			translating: "Translation in progress",
			someFailed: "Something went wrong",
			/* The chat's head: the call's state in words, and since when. */
			call: {
				active: "Call in progress",
				passive: "Call in progress, passive",
				inactive: "Call ended",
				since: "since {time}",
				/* How long an ended call took: "1 min 11 s"; seconds are dropped once there are hours. */
				length: {
					seconds: "{s} s",
					minutes: "{min} min {s} s",
					hours: "{h} h {min} min",
				},
			},
			/* How a message came in: the name of the icon in its header row. */
			type: {
				speech: "Spoken",
				text: "Typed",
				rtt: "Real-time text",
				prefab: "Prefab",
				dtmf: "Keypad tones",
				other: "System",
			},
			role: "Dispatcher | Caller | Caller {caller}",
			summary: "Summary",
			summaryNote: "AI-generated summary — verify critical information.",
			messagesNote:
				"AI-powered transcription, translation and synthesis — verify critical information.",
			/* The note in the chat's head, on one line; its whole sentence is the tooltip. */
			messagesNoteShort: "AI-assisted — check critical details",
			showMore: "Show more",
			showLess: "Show less",
			follow: "Follow the conversation",
			language: {
				search: "Search languages…",
				noMatch: "No language matches.",
				detect: "Detect automatically",
				mentioned: "Mentioned languages",
				other: "All languages",
				locked: "Language fixed — automatic detection will not change it. Click to allow detection.",
				unlocked: "Detected language — detection may still change it. Click to fix it.",
				lockedReadonly: "Language fixed — automatic detection will not change it.",
				unlockedReadonly: "Detected language — detection may still change it.",
			},
			play: {
				play: "Play the translation to the caller",
				again: "Play the translation again",
				whenReady: "Play as soon as the translation is ready",
				queued: "Plays as soon as the translation is ready — click to cancel",
			},
			composer: {
				label: "Reply",
				write: "Write",
				send: "Send",
				placeholder: "Write a message that will be read out loud…",
				groupEmpty: "This group has no prefabs yet.",
				setLanguageFirst: "Set the dispatcher's language to use prefabs.",
			},
			failed: {
				setLanguage: "The language could not be changed",
				sendText: "The message could not be sent",
				sendPrefab: "The prefab could not be played",
				play: "The audio could not be played",
				because: "{action}: {reason}",
				featureDisabled: "this process does not allow it",
				noSession: "the process is not connected to a call",
				timeout: "the process did not answer",
			},
		},
		// The suggestion pool: the suggestions a process has made that are still open.
		pool: {
			title: "Suggestions",
			empty: "No suggestions are open.",
			applyFailed: "The suggestion could not be applied.",
			/* Between a field's best value and its alternatives. */
			or: "or",
			/* A value the process marked as not known. */
			unknown: "Unknown",
			/* Beside the title: how many fields are open. */
			count: "{n} open",
			/* On a field's card: what it holds now. */
			now: "Now: {value}",
			/* A value, as a screen reader names it: what it is, and where it goes. */
			apply: "{value}, set as {target}",
			/* After the first three values: the rest, and folding them away again. */
			more: "+{n} more",
			fewer: "Fewer",
			dismiss: "Dismiss the suggestions for {target}",
			/* The × on a card the process refused: closes the note; the field, still asked for, is open again. */
			closeRefusal: "Close the note on {target}",
			/* A persistent card with no suggestion yet: its row, and before the chips of a strip. */
			waitingShort: "waiting",
			/* Applied, and not taken by the process yet. */
			waiting: "Waiting for the process …",
			/* Taken: on the card, and said to a screen reader. */
			settled: "Set to {value}",
			announce: "{target} set to {value}",
			notTaken: "The process did not take this value.",
			/* The process refused the value, and said why. */
			refused: "The process did not take this value: {reason}",
			/* Under the values: why — a value's own reason, else where it came from. */
			source: {
				process: "Suggested by the process",
				model: "Suggested by AI",
				provider: "Found in a data source",
			},
		},
		notifications: {
			/* The landmark around the notifications. */
			region: "Notifications",
			dismiss: "Dismiss notification",
			fold: "Collapse notifications",
			/* The folded pill: one line with the current one's headline. */
			unfold: "Show notifications ({n}): {title}",
			// While something is queued behind the one on screen: the countdown until it comes. Only
			// an untitled card says it; a titled one's countdown is named by its title.
			progress: "Time until the next notification",
			/* The card's pager, through all of them in the order they arrived. */
			previous: "Previous notification",
			next: "Next notification",
			position: "{n} of {total}",
			/* On the card and the pill: how many have arrived and not been shown yet. */
			waiting: "+{n} new",
			/* Said by a screen reader when the operator pages to another one. */
			stepped: "{n} of {total}: {title}",
			/* A notification with neither a title nor a body. */
			untitled: "Notification",
			/* Said by a screen reader as one arrives — an error at once, the rest when it is quiet. */
			announce: "New notification: {title}",
			announceError: "Error: {title}",
			announceMany: "{n} new notifications",
		},
		view: {
			needsManage:
				"Your role does not let you stop or restart processes, or change how they restart.",
			backToProcesses: "Back to the processes",
			// The grid the panels are laid out in: what a screen reader calls each part of it.
			column: { left: "left column", middle: "middle column", right: "right column" },
			align: { top: "top", bottom: "bottom" },
			slot: "Panels, {column}, {align}",
			resizeColumns: "Width of the {before} and the {after}",
			resizeRows: "Height of the top and the bottom of the {column}",
			questionsOrHistory: "Questions or their history",
			questionTabs: "Question tabs",
			closeTab: "Close {name}",
			closeLogs: "Close the log",
			resizeLogs: "Height of the log",
			graphly: "Graphly",
			graphlyPlaceholder: "Waiting for process data…",
			questions: "Questions",
			lumina: "Lumina",
			// The chart's own log of changes, opened from its feed, and the search over a long list of values.
			luminaLog: "Log",
			luminaSearch: "Search…",
			logs: "Logs",
			noLogs: "No log output yet.",
			noDataobjects: "No dataobjects currently.",
			noQuestions: "No questions currently.",
			history: "History ({count})",
			defaultQuestionTab: "General",
			onTopGroup: "Pinned",
			savedState: "Saved state from {date} · this process is not running",
			/* The process's state in its view's header. */
			status: {
				starting: "Process starting",
				running: "Process running",
				stopped: "Process stopped",
				failed: "Process failed",
				archived: "Process archived",
			},
			/* Below the width the process's layout needs: this, instead of the process. */
			tooNarrow: "Window too narrow",
			tooNarrowNote: "This view needs at least {px} px of width.",
		},
		question: {
			completed: "Answered — only fields marked editable still take changes",
			/* The two answers to a yes-or-no question. */
			yes: "Yes",
			no: "No",
			/* Beside an answer: whether it is written to the process yet. */
			status: {
				saved: "Saved",
				saving: "Saving …",
				unsent: "Not saved yet — press Enter or leave the field",
				unsentList: "Not added yet — press Enter or leave the field",
			},
			/* What an empty field says to do, by its kind. */
			placeholder: {
				text: "Enter text",
				number: "Enter a number",
				choose: "Choose …",
				listFree: "Type, then Enter to add",
				listPick: "Choose one or more",
			},
			/* A list answer: chips typed in, or picked from a Type's values. */
			list: {
				add: "Add “{value}”",
				typeToAdd: "Type an entry, then press Enter",
				noMatch: "No value matches",
				notANumber: "“{value}” is not a number — only numbers can be added",
			},
			/* A dataobject answer as a form: what a partial answer still lacks. */
			form: {
				empty: "This dataobject has nothing to fill in",
				missing: "Still needed: {fields}",
			},
			/* A question its model bound to other people: why its inputs are locked. */
			boundToOthers: "Only the people this question is for may answer it.",
			/* A NotitiaUser input: a person of the dataspace, and the prompt to share the process with
			   them when they cannot see it, or cannot take part in it. */
			user: {
				placeholder: "Search by name or email…",
				nobody: "Nobody found.",
				loadFailed: "The people of this dataspace could not be loaded.",
				cannotSee: "{name} cannot see this process.",
				cannotTakePart: "{name} can see this process but cannot take part in it.",
				shareTitle: "Share this process with {name}?",
				shareCannotSee:
					"{name} cannot see this process: their role does not show it. Share it with them, or choose them without sharing it.",
				shareCannotTakePart:
					"{name} can see this process, but their role does not let them take part in it. Let them take part, or choose them as they are.",
				dontShare: "Choose without sharing",
			},
			/* A Location input: its address, a search, and a map whose pin answers it. */
			location: {
				none: "No address yet",
				/* A partial answer: the parts it lacks, by the designer's terms. */
				missing: "Still needed: {fields}",
				search: "Search for an address",
				searchPlaceholder: "Street, number, town",
				searching: "Searching …",
				results: "Addresses found",
				noResults: "No address found",
				searchFailed: "The address search cannot be reached right now",
				showMap: "Show map",
				hideMap: "Hide map",
				pinHint: "Click the map or drag the pin to set the place",
				approximate: "Approximate place, looked up from the address",
				placing: "Looking up the address …",
				addressFailed:
					"No address could be found for this spot — its coordinates were taken",
				map: "Map for {label}",
				pin: "Place for {label}",
			},
		},
		history: {
			recorded: "Recorded {at} · revision {revision}",
			removed: "Removed {at}, before it was complete",
			/* A history card's badge: how far the question was answered before it was removed. */
			answered: {
				not: "Not answered",
				partly: "Partly answered",
			},
			/* Under a field of a history entry that was left without an answer. */
			noAnswer: "No answer",
		},
	},
};
